import type { BattleSnapshot, DebugSnapshot, Facing } from '../../core';
import type { PresentationCue } from './phaser-battle-adapter';
import type { StickPresentationState, StickViewModel } from './stick-figure-model';
import { resolveSkillHitEffect } from '../skill/skill-presentation-model';
import type { SkillHitEffectKind } from '../skill/skill-presentation-model';

export type CombatVisualEffectKind =
  | 'HIT_SPARK'
  | 'HEAVY_HIT'
  | 'GUARD_RING'
  | 'COUNTER_FLASH'
  | 'SKILL_EFFECT'
  | 'SUPER_FLASH'
  | 'KO_BURST'
  | 'SALES_SUPER_HIT'
  | 'DEV_SUPER_HIT'
  | SkillHitEffectKind;

export interface CombatVisualEffect {
  readonly sequence: number;
  readonly kind: CombatVisualEffectKind;
  readonly x: number;
  readonly y: number;
  readonly color: number;
  readonly size: number;
  readonly expiresAtMs: number;
}

export interface AttackTrailModel {
  readonly fromX: number;
  readonly toX: number;
  readonly y: number;
  readonly color: number;
  readonly width: number;
}

export interface TrapVisualModel {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

const numericPlayer = (value: unknown): 0 | 1 | null => (value === 0 || value === 1 ? value : null);
const effectPlayer = (cue: PresentationCue): 0 | 1 | null => {
  if (cue.kind === 'KO') return numericPlayer(cue.payload.loser);
  if (cue.kind === 'COUNTER') return numericPlayer(cue.payload.attacker);
  return numericPlayer(cue.payload.defender ?? cue.payload.target ?? cue.payload.player);
};

export const cueToCombatEffect = (cue: PresentationCue, snapshot: BattleSnapshot, nowMs: number): CombatVisualEffect | null => {
  const player = effectPlayer(cue);
  const character = player === null ? null : snapshot.characters[player];
  const root = character?.position ?? { x: 640, y: 360 };
  const attackId = String(cue.payload.attackId ?? '');
  const hitId = String(cue.payload.hitId ?? '');
  let kind: CombatVisualEffectKind;
  let color = 0xfff2a8;
  let size: number;

  switch (cue.kind) {
    case 'HIT': {
      // 1. Check character-specific Super hits
      if (attackId.includes('sales-super') || (attackId.includes('super') && !attackId.includes('developer'))) {
        kind = 'SALES_SUPER_HIT';
        color = 0xfde047;
        size = 80;
      } else if (attackId.includes('developer-super') || attackId.includes('super')) {
        kind = 'DEV_SUPER_HIT';
        color = 0x38bdf8;
        size = 70;
      } else {
        // 2. Check character-specific Skill hits
        const skillHit = resolveSkillHitEffect(attackId, hitId);
        if (skillHit !== null) {
          kind = skillHit.kind;
          color = skillHit.color;
          size = skillHit.size;
        } else if (attackId.includes('heavy')) {
          kind = 'HEAVY_HIT';
          size = 58;
        } else {
          kind = 'HIT_SPARK';
          size = 32;
        }
      }
      break;
    }
    case 'GUARD':
      kind = 'GUARD_RING';
      color = 0x67d4ff;
      size = 42;
      break;
    case 'COUNTER':
      kind = 'COUNTER_FLASH';
      color = 0xffffff;
      size = 72;
      break;
    case 'EFFECT':
      kind = 'SKILL_EFFECT';
      color = String(cue.payload.effectId ?? '').startsWith('sales') ? 0xff8a3d : 0x45a9ff;
      size = 64;
      break;
    case 'CINEMATIC':
      return null;
    case 'KO':
      kind = 'KO_BURST';
      color = 0xff4664;
      size = 95;
      break;
    default:
      return null;
  }

  return Object.freeze({
    sequence: cue.sequence,
    kind,
    x: root.x,
    y: root.y - 110,
    color,
    size,
    expiresAtMs: nowMs + Math.max(250, (cue.durationFrames * 1_000) / 60),
  });
};

const attacking = (state: StickPresentationState): boolean =>
  ['lightAttack', 'heavyAttack', 'airAttack', 'skill1', 'skill2', 'super'].includes(state);

export const createAttackTrail = (view: StickViewModel, rootX: number, rootY: number, facing: Facing): AttackTrailModel | null => {
  if (!attacking(view.pose.state) || view.pose.attackPhase !== 'ACTIVE') return null;
  const direction = facing === 'RIGHT' ? 1 : -1;
  const reach = Math.abs(view.pose.rightHand.x);
  return Object.freeze({
    fromX: rootX + direction * 12,
    toX: rootX + direction * reach,
    y: rootY + view.pose.rightHand.y,
    color: view.profile.accentColor,
    width: view.pose.state === 'heavyAttack' || view.pose.state === 'super' ? 12 : 6,
  });
};

export const projectTrapVisuals = (debug: DebugSnapshot): readonly TrapVisualModel[] =>
  Object.freeze(
    debug.boxes
      .filter(({ type }) => type === 'TRAP')
      .map(({ box }) =>
        Object.freeze({
          x: (box.minX + box.maxX) / 2,
          y: box.maxY,
          width: box.maxX - box.minX,
          height: box.maxY - box.minY,
        }),
      ),
  );
